import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Dispatch } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  TextField
} from "@mui/material";

import { RegisterUserInput, RegisterUserSchema } from "../../lib/validations/user.schema";
import { monthDropDown, yearDropDown, paymentImageURLs, stateInfo } from "../../config/static-data";
import SelectInput from "../../components/custom-input/SelectInput";
import PasswordInput from "../../components/custom-input/PasswordInput";
// import AuthBannerImage from "../../components/form/AuthBannerImage";
import ButtonLoader from "../../components/shared/ButtonLoader";
import { ReduxResponse } from "../../types/store";
import { registerUser } from "../../redux/actions/cognitouser.action";
import { clearFormat } from "../../utils/functions";
import UserLicenseDialog from "../../components/user/UserLicenseDialog";

export default function RegisterPage() {
  const navigate = useNavigate();
  const dispatch: Dispatch = useDispatch();
  const { user, isLoading } = useSelector(({ cognitouserReducer }) => cognitouserReducer);

  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState<boolean>(true);
  const [selectedState, setSelectedState] = useState("");

  // Destructure form hooks for the input
  const methods = useForm<RegisterUserInput>({ resolver: zodResolver(RegisterUserSchema) });
  const { register, handleSubmit, reset, formState, setValue, watch } = methods;
  const { errors } = formState;

  const expiredMonth = watch("expireMonth");
  const expiredYear = watch("expireYear");

  useEffect(() => {
    if (!user || !user.email) {
      navigate("/user/login");
    }
    if (!user || !user.UserStatus || user.UserStats === "COMPLETED") {
      navigate("/user/login");
    }
  }, []);

  // Handle register form submit
  function onSubmit(data: RegisterUserInput) {
    const registerData: RegisterUserInput = {
      ...data,
      creditNumber: clearFormat(data.creditNumber)
    };

    dispatch(registerUser(registerData))
      .then((response: ReduxResponse) => {
        if (response.success) {
          // toast.success(response.message);
          toast.success("Congratulations! You have created an account. You will be prompted to login using your username and password.");
          reset();
          navigate("/user/login");
        } else {
          toast.error(response.message ?? "Error in registering user's information! Please input correct information.");
        }
      })
      .catch((error: any) => toast.error(error.message ?? "Error in registering user's information! Please input correct information."));
  }

  return (
    <main className="flex flex-row-reverse justify-center items-center w-full h-screen">
      <div className="flex justify-center items-center w-full h-full md:p-4 bg-secondary">
        <div className="w-auto h-full p-8 overflow-auto">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col justify-center fade-right-anim anim-500"
          >
            <h1 className="text-4xl mb-8">ACCOUNT SIGN UP</h1>

            <div className="flex flex-wrap w-full md:max-w-2xl">
              <div className="flex flex-wrap w-full gap-2">
                <TextField
                  label="First Name"
                  variant="outlined"
                  className="w-full my-2 flex-1"
                  {...register("firstName")}
                  error={errors.firstName ? true : false}
                  helperText={errors.firstName?.message}
                />

                <TextField
                  label="Middle Name"
                  variant="outlined"
                  className="w-full md:mx-4 my-2 !hidden"
                  {...register("middleName")}
                  error={errors.middleName ? true : false}
                  helperText={errors.middleName?.message}
                />

                <TextField
                  label="Last Name"
                  variant="outlined"
                  className="w-full my-2 flex-1"
                  {...register("lastName")}
                  error={errors.lastName ? true : false}
                  helperText={errors.lastName?.message}
                />
              </div>
              <div className="flex flex-wrap w-full gap-2">
                <TextField
                  label="Firm Name"
                  variant="outlined"
                  className="w-full min-w-[280px] my-2 flex-1"
                  {...register("firmName")}
                  error={errors.firmName ? true : false}
                  helperText={errors.firmName?.message}
                />

                <TextField
                  label="Firm's website"
                  variant="outlined"
                  className="w-full min-w-[280px] my-2 flex-1"
                  {...register("firmDomain")}
                  error={errors.firmDomain ? true : false}
                  helperText={errors.firmDomain?.message}
                />
              </div>

              <TextField
                label="Email"
                variant="outlined"
                value={user ? user.email : ""}
                className="w-full my-2"
                {...register("email")}
                error={errors.email ? true : false}
                helperText={errors.email?.message}
              />

              <TextField
                label="Address 1"
                variant="outlined"
                className="w-full my-2"
                {...register("address1")}
                error={errors.address1 ? true : false}
                helperText={errors.address1?.message}
              />

              <TextField
                label="Address 2"
                variant="outlined"
                className="w-full my-2"
                {...register("address2")}
                error={errors.address2 ? true : false}
                helperText={errors.address2?.message}
              />

              <TextField
                label="City"
                variant="outlined"
                className="w-full md:w-64 my-2"
                {...register("city")}
                error={errors.city ? true : false}
                helperText={errors.city?.message}
              />

              <SelectInput
                label="State"
                variant="outlined"
                className="w-28 md:w-32 md:mx-4 my-2"
                handler={register("state")}
                error={errors.state ? true : false}
                helperText={errors.state?.message}
                options={{
                  data: stateInfo,
                  labelKey: "abbreviation",
                  valueKey: "abbreviation",
                  value: selectedState
                }}
                onChange={(e) => { setSelectedState(e.target.value); }}
              />

              <TextField
                label="Zip Code"
                variant="outlined"
                className="w-44 md:w-32 ml-4 md:ml-0 my-2"
                {...register("zip")}
                error={errors.zip ? true : false}
                helperText={errors.zip?.message}
              />

              <div className="w-full max-w-2xl bg-secondary my-4">
                <h1 className="text-xl font-semibold mb-1">Payment Methods</h1>
                <p className="text-sm text-gray-500 mb-4">
                  ※ Credit card information is not required for the account creation. You enter it when you place an order.
                </p>

                <div className="flex items-center mb-6">
                  {paymentImageURLs.map((src: string, index: number) => (
                    <img key={index} src={src} alt="method_image" width={48} height={30} className="mx-1" />
                  ))}
                </div>

                <TextField
                  label="Card Owner Name"
                  variant="outlined"
                  className="w-full mb-4"
                  {...register("creditOwner")}
                  error={!!errors.creditOwner}
                  helperText={errors.creditOwner?.message}
                />

                <TextField
                  label="Credit Card Number"
                  variant="outlined"
                  className="w-full mb-4"
                  {...register("creditNumber")}
                  error={!!errors.creditNumber}
                  helperText={errors.creditNumber?.message}
                />

                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[100px]">
                    {/* <h2 className="text-sm text-gray-500 mb-1">Expiration Date</h2> */}
                    <div className="flex gap-2">
                      <SelectInput
                        label="MM"
                        className="w-[100px]"
                        handler={register("expireMonth")}
                        error={!!errors.expireMonth}
                        helperText={errors.expireMonth?.message}
                        options={{ data: monthDropDown, value: expiredMonth }}
                        onChange={(e) => setValue("expireMonth", e.target.value)}
                      />

                      <SelectInput
                        label="YYYY"
                        className="w-[100px]"
                        handler={register("expireYear")}
                        error={!!errors.expireYear}
                        helperText={errors.expireYear?.message}
                        options={{ data: yearDropDown, value: expiredYear }}
                        onChange={(e) => setValue("expireYear", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="min-w-[100px] flex-1">
                    <TextField
                      label="Zip Code"
                      variant="outlined"
                      className="w-full"
                      {...register("creditZip")}
                      error={!!errors.creditZip}
                      helperText={errors.creditZip?.message}
                    />
                  </div>

                  <div className="min-w-[100px] flex-1">
                    <PasswordInput
                      label="CVV"
                      className="w-full"
                      handler={register("creditCode")}
                      error={!!errors.creditCode}
                      helperText={errors.creditCode?.message}
                    />
                  </div>
                </div>
              </div>


              <PasswordInput
                label="Password"
                className="w-full my-2"
                handler={register("password")}
                error={errors.password ? true : false}
                helperText={errors.password?.message}
              />

              <PasswordInput
                label="Confirm Password"
                className="w-full my-2"
                handler={register("confirm")}
                error={errors.confirm ? true : false}
                helperText={errors.confirm?.message}
              />

            </div>

            <div className="flex items-center mt-2">
              <u className="hover:cursor-pointer" onClick={() => setIsLicenseModalOpen(true)}>Master Service Agreement</u>.
            </div>

            <Button
              type="submit"
              variant="contained"
              className="w-80 h-12 my-8 bg-primary font-bold tracking-widest"
              disabled={isLoading}
            >
              {isLoading ? <ButtonLoader /> : "register"}
            </Button>

            <div className="flex items-center my-2">
              <p>Go back to </p>
              <Link to={"/"} className="mx-2 underline hover:text-blue-500">Homepage</Link>
            </div>
          </form>
        </div>
      </div>

      <div style={{
        width: "70%",
        height: "100%",
        backgroundImage: "url(/assets/images/banners/auth-banner.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        animation: "fade-in-anim 1s"
      }} className="hidden lg:block">
      </div>
      <UserLicenseDialog open={isLicenseModalOpen} onClose={() => setIsLicenseModalOpen(false)} />
    </main>
  );
}
